import { useEffect, useMemo, useState } from "react";
import { formatTimestamp, isWeekend } from "./common";

// 各市场的交易时段按市场当地时间定义（分钟数）
const MARKET_META = {
    US: {
        label: "美股",
        timeZone: "America/New_York",
        sessions: [[9 * 60 + 30, 16 * 60]],
    },
    CN: {
        label: "A股",
        timeZone: "Asia/Shanghai",
        sessions: [[9 * 60 + 30, 11 * 60 + 30], [13 * 60, 15 * 60]],
    },
    HK: {
        label: "港股",
        timeZone: "Asia/Hong_Kong",
        sessions: [[9 * 60 + 30, 12 * 60], [13 * 60, 16 * 60]],
    },
};

const STATUS_TEXT = {
    open: "交易中",
    break: "午间休市",
    preopen: "未开盘",
    closed: "已收盘",
    weekend: "周末休市",
};

function getZonedParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).formatToParts(date);
    const map = {};
    parts.forEach((part) => {
        if (part.type !== "literal") {
            map[part.type] = part.value;
        }
    });
    const year = Number(map.year);
    const month = Number(map.month);
    const day = Number(map.day);
    const hour = Number(map.hour) % 24;
    const minute = Number(map.minute);
    return {
        year,
        month,
        day,
        hour,
        minute,
        minutes: hour * 60 + minute,
        // 0=周日，1-6=周一至周六
        weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
    };
}

// 市场时区相对 UTC 的偏移（分钟）
function getTimeZoneOffsetMinutes(date, timeZone) {
    const parts = getZonedParts(date, timeZone);
    const utcMinute = Math.floor(date.getTime() / 60000) * 60000;
    const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
    return Math.round((asUTC - utcMinute) / 60000);
}

function formatMinutes(totalMinutes) {
    const normalized = ((totalMinutes % 1440) + 1440) % 1440;
    const hour = Math.floor(normalized / 60);
    const minute = normalized % 60;
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

// 把市场当地时段换算成本地时间；跨午夜时标注“次日”
function formatSessionRange(startMinutes, endMinutes, shiftMinutes) {
    const start = startMinutes + shiftMinutes;
    const end = endMinutes + shiftMinutes;
    const startDay = Math.floor(start / 1440);
    const endDay = Math.floor(end / 1440);
    const prefix = startDay < 0 ? "前一日" : "";
    const nextDay = endDay > startDay ? "次日" : "";
    return `${prefix}${formatMinutes(start)}–${nextDay}${formatMinutes(end)}`;
}

function getMeta(market) {
    return MARKET_META[market] || MARKET_META.US;
}

/**
 * 当前市场交易状态，以及换算成用户本地时间的交易时段。
 * 周末按市场当地时间判断（例如北京时间周六上午，美股仍是周五）。
 */
export function getMarketSessionInfo(market, now = new Date()) {
    const meta = getMeta(market);
    const parts = getZonedParts(now, meta.timeZone);
    const sessions = meta.sessions;
    const isWeekendDay = parts.weekday === 0 || parts.weekday === 6;

    let status;
    if (isWeekendDay) {
        status = "weekend";
    } else if (sessions.some(([start, end]) => parts.minutes >= start && parts.minutes < end)) {
        status = "open";
    } else if (sessions.length > 1
        && parts.minutes >= sessions[0][1]
        && parts.minutes < sessions[1][0]) {
        status = "break";
    } else if (parts.minutes < sessions[0][0]) {
        status = "preopen";
    } else {
        status = "closed";
    }

    const marketOffset = getTimeZoneOffsetMinutes(now, meta.timeZone);
    const localOffset = -now.getTimezoneOffset();
    const shiftMinutes = localOffset - marketOffset;
    const rangeText = sessions
        .map(([start, end]) => formatSessionRange(start, end, shiftMinutes))
        .join(" / ");

    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let timeZoneLabel = "本地时间";
    if (localTimeZone === "Asia/Shanghai" || localTimeZone === "Asia/Chongqing") {
        timeZoneLabel = "北京时间";
    } else if (localTimeZone === meta.timeZone) {
        timeZoneLabel = "当地时间";
    }

    return {
        status,
        statusText: STATUS_TEXT[status],
        isOpen: status === "open",
        marketLabel: meta.label,
        timeZone: meta.timeZone,
        timeZoneLabel,
        rangeText,
        latestTradingTimestamp: getLatestTradingTimestamp(market, now),
    };
}

/**
 * 当前可选的最近一个交易日（本地零点的时间戳）。
 * 以市场当地日期为准：北京时间周六上午，美股最近交易日仍是周五。
 */
export function getLatestTradingTimestamp(market, now = new Date()) {
    const meta = getMeta(market);
    const parts = getZonedParts(now, meta.timeZone);
    let offsetDays = 0;
    if (parts.weekday === 6) {
        offsetDays = 1;
    } else if (parts.weekday === 0) {
        offsetDays = 2;
    }
    return new Date(parts.year, parts.month - 1, parts.day - offsetDays).getTime();
}

export function isTradingWeekday(timestamp) {
    return !isWeekend(timestamp);
}

export function isSameTradingDay(a, b) {
    if (!a || !b) {
        return false;
    }
    return formatTimestamp(a) === formatTimestamp(b);
}

// 下一个交易日，不允许超过 latest
export function getNextTradingTimestamp(current, latest) {
    const base = new Date(current);
    base.setHours(0, 0, 0, 0);
    let next = base.getTime() + 86400000;
    while (isWeekend(next)) {
        next += 86400000;
    }
    return formatTimestamp(next) > formatTimestamp(latest) ? latest : next;
}

export function useMarketSession(market) {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const update = () => setNow(new Date());
        const timer = setInterval(update, 30000);
        const onVisibilityChange = () => {
            if (!document.hidden) {
                update();
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    return useMemo(() => getMarketSessionInfo(market, now), [market, now]);
}
